/** @jsx jsx */
import React, { useContext, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider, jsx } from '@front10/base-elements';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import { isSSR } from '@front10/utils';
import nanoid from 'nanoid';
import { EmotionCache } from '../../containers/_commons/emotionCache';

/**
 * Atoms
 */
import { FilterButton } from '../../lab/FilterButton';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import Checkbox from '../InlineFilters/CheckboxItem';
/**
 * Controls
 */

const styles = {
  menu: {
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    listStyle: 'none',
    position: 'relative'
  }
};

const RateTagsFilter = (props, ref) => {
  const {
    onChange,
    values = [],
    order = 'asc',
    selectedItems: selectedItemsState = [],
    disabled,
    filterButtonIconProps,
    doneButtonLabelKey = 'cmp-rate-tags-done-button',
    clearButtonLabelKey = 'cmp-rate-tags-clear-button',
    titleKey = 'cmp-rate-tags-filter-title',
    placeholderLabelKey = 'cmp-rate-tags-placeholder',
    popover = {},
    isMobile,
    allItems = { type: 'li' },
    listItem,
    separatorStyle = {
      borderBottom: true
    },
    countSelectedStyle = {
      'cmp-styles': {
        'font-weight': 'bold'
      }
    },
    selectedStyle = {
      'cmp-styles': {
        'font-weight': '300'
      }
    },
    clearButtonSettings,
    doneButtonSettings,
    footerContainer = {},
    buttonsContainer = {},
    count = true,
    labelKeySeparatorMultiSelection = 'cmp-separator-comma',
    showDescriptionLabel = false
  } = props;

  const ITEM_WEIGHT_ELEMENTS = -1;
  const ITEM_ANY_ELEMENTS = 'any';
  const [id] = React.useState(nanoid());
  const [disabledDoneButton, setDisabledDoneButton] = useState(true);

  const flexibleRef = React.useRef();
  const doneButtonRef = React.useRef();
  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const { alwaysEnabledDoneButton = true } = doneButtonSettings;
  const title = translate(titleKey, 'Offers');

  const ITEM_ALL_ELEMENTS = React.useMemo(() => {
    return {
      name: translate('cmp-rate-tags-all', 'All Tags'),
      key: 'value0',
      value: ITEM_ANY_ELEMENTS
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ITEM_WEIGHT_ELEMENTS, translate]);

  const getOptions = (options) => {
    return options
      .map((option) => ({
        label: translate(option.name, option.name),
        value: option.value,
        key: option.key
      }))
      .sort(function (a, b) {
        var itemA = a.label.toLowerCase(),
          itemB = b.label.toLowerCase();
        let comparison = 0;
        if (itemA < itemB) {
          comparison = -1;
        } else if (itemA > itemB) {
          comparison = 1;
        }
        if (a.value === ITEM_ANY_ELEMENTS || b.value === ITEM_ANY_ELEMENTS) {
          comparison = 0;
        }
        return order === 'desc' ? comparison * -1 : comparison;
      });
  };

  const options = React.useMemo(() => {
    return getOptions(values ? [ITEM_ALL_ELEMENTS, ...values] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const selectedItemsList = React.useMemo(() => {
    const selected = selectedItemsState.map((item) =>
      typeof item === 'string' ? options.find((tag) => tag.value === item) : item
    );
    return selected;
  }, [selectedItemsState, options]);

  const [selectedItems, setSelectedItems] = React.useState(() => {
    if (Boolean(selectedItemsState)) {
      return selectedItemsList;
    } else {
      return [];
    }
  });

  const update = React.useCallback(() => {
    return setSelectedItems(Boolean(selectedItemsState) ? selectedItemsList : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemsState]);

  const focusToggleElement = () => {
    if (!isSSR()) {
      document.querySelector(`#toggleElementId-${id}`).focus();
    }
  };

  const result = options.map((item) => {
    return renderItem(item);
  });

  const selectItem = (itemMarked) => {
    if (itemMarked.value === ITEM_ANY_ELEMENTS) {
      if (selectedItems.length === options.length) {
        return [];
      } else {
        return options;
      }
    } else {
      if (selectedItems.length === 0) {
        return [itemMarked];
      } else {
        const index = selectedItems.findIndex((item) => item.value === itemMarked.value);
        if (index !== -1 && itemMarked.value !== ITEM_ANY_ELEMENTS) {
          let aux = [...selectedItems];
          aux.splice(index, 1);
          const index1 = aux.findIndex((item) => item.value === ITEM_ALL_ELEMENTS.value);
          if (index1 !== -1) {
            aux.splice(index1, 1);
          }
          return aux;
          // }
        } else {
          if (itemMarked.value === ITEM_ANY_ELEMENTS) {
            return options;
          } else {
            let tmp = [...selectedItems];
            tmp.push(itemMarked);
            if (tmp.length === options.length - 1) {
              return options;
            }
            return tmp;
          }
        }
      }
    }
  };

  const handleClear = () => {
    setSelectedItems([]);
    setDisabledDoneButton(false);
    doneButtonRef.current?.focus();
  };

  function renderItem(item) {
    return (
      <Checkbox
        {...props}
        {...listItem}
        dataTestId={
          item.value === ITEM_ANY_ELEMENTS
            ? 'rates-filter-any'
            : `rates-filter-${item.value.toLowerCase()}`
        }
        allItems={item.value === ITEM_ANY_ELEMENTS ? { ...separatorStyle, ...allItems } : allItems}
        isMobile={isMobile}
        item={item}
        isChecked={selectedItems.find((x) => x.value === item.value)}
        showSideMessage={false}
        onChange={(_, itemMarked) => {
          if (itemMarked.value === ITEM_ANY_ELEMENTS) {
            setDisabledDoneButton(false);
          } else {
            if (
              selectedItems.find((x) => x.value === item.value) !== undefined &&
              selectItem.length === 1
            ) {
              setDisabledDoneButton(false);
            }
          }
          const newSelectedItems = selectItem(itemMarked);
          setSelectedItems(newSelectedItems);
        }}
        enableFocus
        onKeyDown={(e) => {
          if (!e.shiftKey && e.key === 'Tab') {
            const opts = options;
            if (
              item.value === opts[opts.length - 1].value &&
              selectedItems.length === 0 &&
              disabledDoneButton
            ) {
              e.preventDefault();
              flexibleRef.current?.close();
              focusToggleElement();
            }
          }
        }}
      />
    );
  }

  const renderFooter = () => (
    <Box display="flex" {...footerContainer}>
      <div aria-live="polite" aria-atomic="true">
        {selectedItems.length !== 0 && (
          <>
            <Text dataTestId="rates-filter-counter" {...countSelectedStyle}>
              {selectedItems[0].value === 'any' ? selectedItems.length - 1 : selectedItems.length}
            </Text>
            <Text {...selectedStyle}>
              {translate('cmp-rate-tags-info-options-selected', ' selected')}
            </Text>
          </>
        )}
      </div>
      <Box dataTestId="rates-actions" {...buttonsContainer}>
        <Button
          dataTestId="rates-filter-clear"
          color="light"
          {...clearButtonSettings}
          disabled={selectedItems.length === 0}
          onClick={handleClear}
        >
          {translate(clearButtonLabelKey, 'Clear')}
        </Button>
        <Button
          dataTestId="rates-filter-done"
          color="primary"
          focusReferences={doneButtonRef}
          {...doneButtonSettings}
          disabled={
            alwaysEnabledDoneButton ? false : selectedItems.length === 0 && disabledDoneButton
          }
          onClick={() => {
            if (flexibleRef.current?.close) {
              onChange(selectedItems);
              flexibleRef.current?.close();
            } else {
              flexibleRef.current?.save();
            }
            focusToggleElement();
          }}
        >
          {translate(doneButtonLabelKey, 'Done')}
        </Button>
      </Box>
    </Box>
  );

  const buttonAriaLabel = React.useMemo(() => {
    if (selectedItemsState.length === 0 && !showDescriptionLabel) {
      return translate(placeholderLabelKey, 'Filters');
    } else {
      return `${selectedItemsList
        .filter((i) => i.value !== 'any')
        .map((i) => i.label)
        .sort()
        .join(`${translate(labelKeySeparatorMultiSelection, ',')} `)}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.length,
    labelKeySeparatorMultiSelection,
    selectedItemsList,
    selectedItemsState,
    translate
  ]);
  const buttonLabel = React.useMemo(() => {
    const selectItem = selectedItemsList.filter((i) => i.value !== 'any');

    if (selectedItemsState.length === 0 && !showDescriptionLabel) {
      return translate(placeholderLabelKey, 'Filters');
    } else {
      return `${selectItem.length >= 1 ? selectItem[0].label : ''}${
        selectItem.length > 1 ? ', ...' : ''
      }`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.length,
    labelKeySeparatorMultiSelection,
    selectedItemsList,
    selectedItemsState,
    translate
  ]);

  const active = React.useMemo(() => {
    return selectedItemsState === undefined || selectedItemsState.length === 0 ? false : true;
  }, [selectedItemsState]);

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <FlexibleFilterContainer
          ref={flexibleRef}
          showSeparator
          saveOnClose={false}
          variant="layout.popover"
          tabIndex={-1}
          label={title}
          title={title}
          footer={renderFooter()}
          enabledDoneButton
          enabledClearButton
          onDismiss={() => {
            update();
          }}
          onOpenPopup={() => {
            update();
            alwaysEnabledDoneButton ? setDisabledDoneButton(false) : setDisabledDoneButton(true);
          }}
          toggleElement={
            <FilterButton
              id={`toggleElementId-${id}`}
              iconSettings={filterButtonIconProps}
              value={`${
                showDescriptionLabel ? translate(placeholderLabelKey, 'Filters') : ''
              } ${buttonLabel}`}
              countValue={selectedItemsList.filter((i) => i.value !== 'any').length}
              labelSeparator={`${translate(labelKeySeparatorMultiSelection, ',')} `}
              aria-label={`${title} ${selectedItemsState.length > 0 ? buttonAriaLabel : ''}`}
              count={count}
              active={active}
              disabled={disabled} // used to disabled the component
              data-testid="rates-filter"
              className="toggle-button-rate-tags"
            />
          }
          onSave={() => onChange(selectedItems)}
          dataTestId="rates-popover"
          {...popover}
        >
          {/* <div tabIndex={0}  ref={listRef} onKeyDown={onKeyDown}> */}
          <ul data-testid="rates-list" style={styles.menu} sx={{ variant: 'menus.panel' }}>
            {result}
          </ul>
          {/* </div> */}
        </FlexibleFilterContainer>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(RateTagsFilter);
